import type {
  ApprovalCalendarItemNode,
  ApprovalRestPeriodNode,
  ApproveWorkPeriodNode,
} from './types.graphql'

const plannedCalendarCategories = new Set(['WORK', 'OVERTIME'])

export type WorkPeriodApprovalRow = {
  type: 'workPeriod'
  id: string
  datetimeStart: string
  datetimeEnd: string
  user: ApproveWorkPeriodNode['user']
  calendarItem: ApproveWorkPeriodNode['calendarItem']
  workPeriod: ApproveWorkPeriodNode
}

export type CalendarItemApprovalRow = {
  type: 'calendarItem'
  id: string
  datetimeStart: string
  datetimeEnd: string
  user: NonNullable<ApprovalCalendarItemNode['user']>
  calendarItem: ApprovalCalendarItemNode
  workPeriod: null
}

export type RestPeriodApprovalRow = {
  type: 'restPeriod'
  id: string
  datetimeStart: string
  datetimeEnd: string
  user: ApproveWorkPeriodNode['user']
  calendarItem: null
  workPeriod: null
  restPeriod: ApprovalRestPeriodNode
}

export type ApprovalRow =
  | WorkPeriodApprovalRow
  | CalendarItemApprovalRow
  | RestPeriodApprovalRow

export function getApprovalRows(
  workPeriods: ApproveWorkPeriodNode[],
  calendarItems: ApprovalCalendarItemNode[],
  restPeriods: ApprovalRestPeriodNode[] = []
): ApprovalRow[] {
  const workPeriodCalendarItemIds = new Set(
    workPeriods
      .map(workPeriod => workPeriod.calendarItem?.id)
      .filter((id): id is string => !!id)
  )

  const workPeriodRows: ApprovalRow[] = workPeriods.map(workPeriod => ({
    type: 'workPeriod',
    id: workPeriod.id,
    datetimeStart: workPeriod.datetimeStart,
    datetimeEnd: workPeriod.datetimeEnd,
    user: workPeriod.user,
    calendarItem: workPeriod.calendarItem,
    workPeriod,
  }))

  const plannedRows: ApprovalRow[] = calendarItems
    .filter(calendarItem => {
      if (!calendarItem.user) return false
      if (!plannedCalendarCategories.has(calendarItem.category)) return false
      if (workPeriodCalendarItemIds.has(calendarItem.id)) return false
      return !calendarItem.workPeriods.edges.some(edge => edge?.node?.id)
    })
    .map(calendarItem => ({
      type: 'calendarItem',
      id: calendarItem.id,
      datetimeStart: calendarItem.start,
      datetimeEnd: calendarItem.end,
      user: calendarItem.user as NonNullable<ApprovalCalendarItemNode['user']>,
      calendarItem,
      workPeriod: null,
    }))

  // A rest takes the identity of the shift it follows: the anchor is what the
  // approver signed off, and it is also what the month report keys the rest's
  // diet and salary day on. Rests whose anchor is not in the list (filtered
  // away, or outside the month) have no place to sit, so they are dropped.
  const workPeriodsById = new Map(
    workPeriods.map(workPeriod => [workPeriod.id, workPeriod])
  )
  const restRows: ApprovalRow[] = restPeriods.flatMap(restPeriod => {
    const anchor = workPeriodsById.get(restPeriod.workPeriodBefore.id)
    if (!anchor) return []
    return [
      {
        type: 'restPeriod' as const,
        id: restPeriod.id,
        datetimeStart: restPeriod.datetimeStart,
        datetimeEnd: restPeriod.datetimeEnd,
        user: anchor.user,
        calendarItem: null,
        workPeriod: null,
        restPeriod,
      },
    ]
  })

  return [...workPeriodRows, ...plannedRows, ...restRows].sort((a, b) =>
    a.datetimeStart.localeCompare(b.datetimeStart)
  )
}
